import ChatBotAvatar from '@/components/Chat/ChatBotAvatar';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import Reanimated, { useSharedValue, useAnimatedStyle, withRepeat, withSequence, withTiming, cancelAnimation } from 'react-native-reanimated';
import ReanimatedSwipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import { useTheme } from '@/context/ThemeContext';
import SidebarListItemLayout from './SidebarListItemLayout';
import SidebarListItemRename from './SidebarListItemRename';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useUIStore, useAuthStore, useCollectionStore, useChatStore, useCoworkerStore } from '@/store';
import { workHasPendingUserGate, workToneColor, workUnreadDotTone, workUnreadDotLabel } from '@/constants/work';
import { useRouter } from 'expo-router';
import PropTypes from 'prop-types';
import ChatDropdown from '@/components/Dropdowns/ChatDropdown';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useChat } from '@/hooks/useChat';
import useResponsiveness from '@/hooks/useResponsiveness';
import getRelativeTime from '@/constants/utils/timestamp';

const createStyles = (colors, showTimestamps, relativeTime) => StyleSheet.create({
  pinIcon: {
    marginRight: 4,
    marginLeft: -2,
  },
  archiveIcon: {
    marginRight: 4,
    marginLeft: 4,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  titleSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  title: {
    flexShrink: 1,
    minWidth: 0,
    // Reserve space for the timestamp only when it's actually rendered;
    // otherwise the title hits 100% and stops getting truncated for no
    // visible reason when the user has timestamps disabled.
    maxWidth: (showTimestamps && relativeTime) ? '85%' : '100%',
  },
  timestamp: {
    marginLeft: 4,
    flexShrink: 0,
  },
  unreadBadgeWrapper: {
    marginLeft: 'auto',
  },
  unreadBadge: {
    backgroundColor: colors.color_destructive,
    borderRadius: 4,
    width: 8,
    height: 8,
    marginLeft: 6,
  },
  generatingBadge: {
    borderRadius: 4,
    width: 8,
    height: 8,
    marginLeft: 6,
  },
  swipeActionsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  swipeAction: {
    width: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  swipeActionArchive: {
    backgroundColor: colors.solid_color_tertiary,
  },
  swipeActionDelete: {
    backgroundColor: colors.color_destructive,
  },
});

// Pulsing accent dot shown on chats with an in-flight generation. Runs on
// the UI thread via Reanimated so it never contends with the JS thread.
const GeneratingDot = React.memo(({ color, style }) => {
  const opacity = useSharedValue(1);

  useEffect(() => {
    opacity.value = withRepeat(
      withSequence(
        withTiming(0.3, { duration: 650 }),
        withTiming(1, { duration: 650 })
      ),
      -1,
      false
    );
    return () => cancelAnimation(opacity);
  }, [opacity]);

  const animatedStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));
  return <Reanimated.View style={[style, animatedStyle, { backgroundColor: color }]} />;
});

GeneratingDot.propTypes = {
  color: PropTypes.string.isRequired,
  style: PropTypes.object,
};

GeneratingDot.displayName = 'GeneratingDot';

function lastWorkAssistantText(chat) {
  const messages = chat?.messages;
  if (!Array.isArray(messages) || messages.length === 0) return '';
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === 'assistant' || message?.role === 'ai') {
      return message.content || message.text || '';
    }
  }
  return '';
}

const StatusIcon = React.memo(({ name, color, style, isXS, hideUntilHover = false }) => {
  const icon = <Icon name={name} size={isXS ? 13 : 12} color={color} style={style} />;
  // Preserve the native icon layout; only web needs a CSS reveal wrapper.
  return Platform.OS === 'web' && hideUntilHover
    ? <View dataSet={{ sidebarChatMetadata: 'true' }}>{icon}</View>
    : icon;
});

StatusIcon.propTypes = {
  name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  style: PropTypes.object,
  hideUntilHover: PropTypes.bool,
};

StatusIcon.displayName = 'StatusIcon';

const TitleContent = React.memo(({
  title,
  agentId,
  isPinned,
  isArchived,
  visibility,
  isCoworkerSession,
  hideMetadataUntilHover,
  showTimestamps,
  relativeTime,
  iconColor,
  styles,
  colors,
  isActive,
  isXS,
  unreadCount,
  isGenerating,
  unreadDotColor,
  unreadDotLabel,
}) => (
    <View style={styles.titleContainer}>
    <View style={styles.titleSection}>
      <ChatBotAvatar agentId={agentId} />
      {isPinned && (
        <StatusIcon
          name="star"
          solid={isPinned}
          color={iconColor}
          style={styles.pinIcon}
          isXS={isXS}
        />
      )}
      <Text 
        numberOfLines={1} 
        size={isXS ? 'm' : 'n'}
        {...(Platform.OS === 'web' && hideMetadataUntilHover ? { dataSet: { sidebarChatTitle: 'true' } } : null)}
        style={[
          styles.title,
          { color: isActive ? colors.accent_text_color : colors.text_color_primary }
        ]}
      >
        {title}
      </Text>
      {isCoworkerSession && (
        <StatusIcon
          name="briefcase"
          color={iconColor}
          style={styles.archiveIcon}
          isXS={isXS}
          hideUntilHover={hideMetadataUntilHover}
        />
      )}
      {isArchived && (
        <StatusIcon
          name="box-archive"
          color={iconColor}
          style={styles.archiveIcon}
          isXS={isXS}
        />
      )}
      {visibility === 'public' && (
        <StatusIcon
          name="globe"
          color={iconColor}
          style={styles.archiveIcon}
          isXS={isXS}
        />
      )}
      {showTimestamps && relativeTime ? (
        <Text 
          size={isXS ? 'm' : 'xs'}
          {...(Platform.OS === 'web' && hideMetadataUntilHover ? { dataSet: { sidebarChatMetadata: 'true' } } : null)}
          style={[
            styles.timestamp,
            {
              color: isActive ? `${colors.accent_text_color}80` : colors.text_color_tertiary
            }
          ]}
        >
          {relativeTime}
        </Text>
      ) : null}
      {/* Notification Bubble */}
      {unreadCount !== undefined && unreadCount > 0 && (
        <View style={styles.unreadBadgeWrapper} accessibilityLabel={unreadDotLabel || 'Unread'}>
          <View style={[styles.unreadBadge, unreadDotColor ? { backgroundColor: unreadDotColor } : null]} />
        </View>
      )}
      {/* In-flight generation indicator (unread badge takes the slot first) */}
      {isGenerating && !(unreadCount > 0) && (
        <View style={styles.unreadBadgeWrapper} accessibilityLabel="Generating response">
          <GeneratingDot color={colors.accent_text_color} style={styles.generatingBadge} />
        </View>
      )}
    </View>
  </View>
));

TitleContent.propTypes = {
  title: PropTypes.string.isRequired,
  isPinned: PropTypes.bool,
  isArchived: PropTypes.bool,
  visibility: PropTypes.string,
  isCoworkerSession: PropTypes.bool,
  hideMetadataUntilHover: PropTypes.bool,
  showTimestamps: PropTypes.bool,
  relativeTime: PropTypes.string,
  iconColor: PropTypes.string.isRequired,
  styles: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  isActive: PropTypes.bool,
  unreadCount: PropTypes.number,
  isGenerating: PropTypes.bool,
  unreadDotColor: PropTypes.string,
  unreadDotLabel: PropTypes.string,
};

TitleContent.displayName = 'TitleContent';

function SidebarListItemChat({
  title,
  agentId,
  isActive = false,
  href,
  imageUrl,
  isPinned: propIsPinned,
  isArchived = false,
  isCoworkerSession = false,
  hideMetadataUntilHover = false,
  visibility,
  lastMessageTime = '',
  id,
  inCollectionId,
  isGenerating = false,
  ...props
}) {
  const { colors } = useTheme();
  const router = useRouter();
  const showTimestamps = useUIStore(state => state.showTimestamps);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const { isXS } = useResponsiveness();
  
  const renameChat = useChatStore(state => state.renameChat);
  const isSelectingMultipleChats = useChatStore(state => state.isSelectingMultipleChats);
  const selectedChatIds = useChatStore(state => state.selectedChatIds);
  const toggleSelectChatForMulti = useChatStore(state => state.toggleSelectChatForMulti);

  // Ensure any open dropdown is closed when toggling multi-select mode,
  // so the row returns to a normal clickable state afterward.
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [isSelectingMultipleChats]);

  // Use the chat hook
  const {
    isPinned,
    chat,
    handleArchiveConfirm,
    handleDeleteConfirm,
  } = useChat(id);

  // Derive a fresh relative timestamp from the chat object on every render so
  // the sidebar timestamps stay live and don't depend on whatever stale value
  // was persisted as `lastMessageTime`. Falls back to props/persisted value
  // for any chat that's missing updatedAt/createdAt.
  const liveRelativeTime = useMemo(() => {
    const ts = chat?.updatedAt || chat?.createdAt;
    return getRelativeTime(ts) || lastMessageTime || '';
  }, [chat?.updatedAt, chat?.createdAt, lastMessageTime]);

  const styles = useMemo(() => 
    createStyles(colors, showTimestamps, liveRelativeTime),
    [colors, showTimestamps, liveRelativeTime]
  );

  const iconColor = useMemo(() => 
    isActive ? colors.accent_text_color : colors.text_color_primary,
    [isActive, colors]
  );

  const isSelected = useMemo(() => selectedChatIds.includes(id), [selectedChatIds, id]);

  const workTaskId = chat?.coworkerSession || null;
  const workTaskStatus = useCoworkerStore((s) => (
    workTaskId ? s.tasks[workTaskId]?.status : undefined
  ));
  const workPendingGate = useCoworkerStore((s) => (
    workTaskId ? workHasPendingUserGate(s.eventsByTask[workTaskId]) : false
  ));
  const workUnreadTone = (isCoworkerSession || workTaskId)
    ? workUnreadDotTone({
      status: workTaskStatus,
      pendingUserGate: workPendingGate,
      lastAssistantText: lastWorkAssistantText(chat),
    })
    : null;
  const unreadDotColor = workUnreadTone
    ? workToneColor(workUnreadTone, colors)
    : undefined;
  const unreadDotLabel = (isCoworkerSession || workTaskId)
    ? workUnreadDotLabel({
      status: workTaskStatus,
      pendingUserGate: workPendingGate,
      lastAssistantText: lastWorkAssistantText(chat),
    })
    : 'Unread';

  const handleToggleSelect = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (inCollectionId) return;
    toggleSelectChatForMulti(id);
  }, [toggleSelectChatForMulti, id]);

  const titleWithPin = useMemo(() => (
    <TitleContent
      title={title}
      agentId={agentId || chat?.agentId}
      isPinned={isPinned}
      isArchived={isArchived}
      visibility={visibility}
      isCoworkerSession={isCoworkerSession}
      hideMetadataUntilHover={hideMetadataUntilHover}
      showTimestamps={showTimestamps}
      relativeTime={liveRelativeTime}
      iconColor={iconColor}
      styles={styles}
      colors={colors}
      isActive={isActive}
      isXS={isXS}
      unreadCount={chat?.unreadCount || 0}
      isGenerating={isGenerating}
      unreadDotColor={unreadDotColor}
      unreadDotLabel={unreadDotLabel}
    />
  ), [
    title, agentId, chat?.agentId, isPinned, isArchived, visibility, isCoworkerSession, hideMetadataUntilHover, showTimestamps,
    liveRelativeTime, iconColor, styles, colors, isActive, isXS, chat?.unreadCount,
    isGenerating, unreadDotColor, unreadDotLabel,
  ]);

  const selectionRightIcon = useMemo(() => {
    if (!isSelectingMultipleChats || inCollectionId) return null;
    return (
      <Pressable onPress={handleToggleSelect} accessibilityRole="checkbox" accessibilityState={{ checked: isSelected }}>
        <Icon
          name={isSelected ? 'square-check' : 'square'}
          size={18}
          color={isSelected ? colors.text_color_primary : colors.text_color_secondary}
        />
      </Pressable>
    );
  }, [isSelectingMultipleChats, inCollectionId, handleToggleSelect, isSelected, isXS, colors]);

  const macOSRightIcon = useMemo(() => {
    if (Platform.OS !== 'macos') return selectionRightIcon;
    if (isSelectingMultipleChats && !inCollectionId) return selectionRightIcon;

    return (
      <DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenu.Trigger>
          <Pressable
            onPress={(e) => {
              e?.preventDefault?.();
              e?.stopPropagation?.();
            }}
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: 4,
            }}
          >
            <Icon
              name="ellipsis"
              size={14}
              color={isActive ? colors.accent_text_color : colors.text_color_secondary}
            />
          </Pressable>
        </DropdownMenu.Trigger>
        <ChatDropdown noRoot conversationId={id} withSelectMultipleChats={true} />
      </DropdownMenu.Root>
    );
  }, [selectionRightIcon, isSelectingMultipleChats, inCollectionId, id, isDropdownOpen, isActive, colors]);

  const handleStartRename = useCallback(() => {
    setIsRenaming(true);
    setIsDropdownOpen(false);
  }, []);

  const handleRenameComplete = useCallback(async (newTitle) => {
    try {
      await renameChat(id, newTitle);
      setIsRenaming(false);
    } catch (error) {
      console.error('Error renaming chat:', error);
    }
  }, [renameChat]);

  const handleRenameCancel = useCallback(() => {
    setIsRenaming(false);
  }, []);

  const dropdownContent = useMemo(() => (
    <ChatDropdown noRoot conversationId={id} withSelectMultipleChats={true} />
  ), [id]);

  const swipeableRef = useRef(null);
  const renderSwipeRightActions = useCallback(() => (
    <View style={styles.swipeActionsRow}>
      <Pressable
        style={[styles.swipeAction, styles.swipeActionArchive]}
        onPress={() => {
          swipeableRef.current?.close();
          handleArchiveConfirm();
        }}
        accessibilityRole="button"
        accessibilityLabel={isArchived ? 'Unarchive chat' : 'Archive chat'}
      >
        <Icon name="box-archive" size={16} color={colors.text_color_primary} />
      </Pressable>
      <Pressable
        style={[styles.swipeAction, styles.swipeActionDelete]}
        onPress={() => {
          swipeableRef.current?.close();
          handleDeleteConfirm();
        }}
        accessibilityRole="button"
        accessibilityLabel="Delete chat"
      >
        <Icon name="trash" size={16} color={colors.text_color_alt} />
      </Pressable>
    </View>
  ), [styles, colors, isArchived, handleArchiveConfirm, handleDeleteConfirm]);

  const content = useMemo(() => (
    <SidebarListItemLayout
      title={titleWithPin}
      isActive={isActive}
      onDropdownStateChange={setIsDropdownOpen}
      dropdownContent={Platform.OS === 'macos' ? undefined : (isSelectingMultipleChats && !inCollectionId ? undefined : dropdownContent)}
      rightIcon={Platform.OS === 'macos' ? macOSRightIcon : selectionRightIcon}
      noEllipsis={Platform.OS === 'macos' || (isSelectingMultipleChats && !inCollectionId)}
      isRightContainerVisible={isSelectingMultipleChats && !inCollectionId}
      onPress={
        isSelectingMultipleChats && !inCollectionId
          ? handleToggleSelect
          : (href && !isDropdownOpen ? () => router.push(href) : undefined)
      }
      accessibilityLabel={`Chat: ${title}`}
      accessibilityRole="button"
      {...(Platform.OS === 'web' && hideMetadataUntilHover ? { dataSet: { sidebarChatRow: 'true' } } : null)}
      accessibilityState={{
        selected: isActive,
        expanded: isDropdownOpen
      }}
      {...props}
    />
  ), [
    titleWithPin, isActive, dropdownContent, isSelectingMultipleChats,
    selectionRightIcon, handleToggleSelect,
    isDropdownOpen, title, props, href, router
  ]);

  if (isRenaming) {
    return (
      <SidebarListItemRename
        initialValue={title}
        onRename={handleRenameComplete}
        onCancel={handleRenameCancel}
      />
    );
  }

  // Swipe actions (native only): quick archive/delete with the same
  // confirmation flow as the dropdown. Disabled while multi-selecting —
  // the checkbox affordance owns the row in that mode.
  const canSwipe = (Platform.OS === 'ios' || Platform.OS === 'android') && !isSelectingMultipleChats;
  if (!canSwipe) {
    return content;
  }

  return (
    <ReanimatedSwipeable
      ref={swipeableRef}
      friction={1.2}
      rightThreshold={36}
      overshootRight={false}
      failOffsetY={[-8, 8]}
      renderRightActions={renderSwipeRightActions}
    >
      {content}
    </ReanimatedSwipeable>
  );
}

SidebarListItemChat.propTypes = {
  title: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  href: PropTypes.string,
  imageUrl: PropTypes.string,
  isPinned: PropTypes.bool,
  isArchived: PropTypes.bool,
  visibility: PropTypes.string,
  hideMetadataUntilHover: PropTypes.bool,
  lastMessageTime: PropTypes.string,
  id: PropTypes.string.isRequired,
  inCollectionId: PropTypes.string,
  unreadCount: PropTypes.number,
  isGenerating: PropTypes.bool,
};

SidebarListItemChat.whyDidYouRender = true;

export default React.memo(SidebarListItemChat);
